import React from "react";
import { Spinner } from "../../../atoms/Spinner";
import {
  HiClock,
  HiCreditCard,
  HiOfficeBuilding,
  HiCurrencyDollar,
  HiDocumentText,
  HiOutlineTrash,
} from "react-icons/hi";
import { CustomButton } from "../../../atoms/CustomButton";
import { Copy } from "../../../atoms/Copy";
import { NoTranslate } from "../../../atoms/NoTranslate";
import UserAvatar from "../../../atoms/UserAvatar";
import SideDrawer from "../../../atoms/SideDrawer";
import { StatusBadge } from "../../../atoms/StatusBadge";
import {
  BalanceResponseDTO,
  PaymentHistoryGroupedDTO,
  PayoutAccountResponseDTO,
  PayoutResponseDTO,
  ServiceWithPlansDTO,
  SumPlanStatusEntryDTO,
} from "../../../../services/paymentModule";
import FeatureLinksSection from "./FeatureLinksSection";
import AcquisitionSection from "./AcquisitionSection";
import EntitySumPlansSection from "./EntitySumPlansSection";
import { GuaranteeTargetPlan } from "./sumPlans";
import {
  EntityDetails,
  FeatureLink,
  formatAmount,
  formatDate,
  getStatusBadge,
} from "./types";

const SummaryChip: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: React.ReactNode;
  loading?: boolean;
  onClick: () => void;
}> = ({ icon, label, value, loading, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm transition-colors hover:border-blue-400 hover:bg-blue-50"
  >
    <span translate="no" className="text-gray-500">
      {icon}
    </span>
    <span className="font-medium text-gray-700">{label}</span>
    {loading ? (
      <Spinner size="sm" />
    ) : (
      <span className="ml-auto text-sm tabular-nums text-gray-900">
        {value ?? "…"}
      </span>
    )}
  </button>
);

interface EntityDrawerProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  entityDetails: EntityDetails | null;

  featureLinks?: FeatureLink[];
  onEmailOwner: () => void;
  onImpersonate: () => void;
  impersonateLoading: boolean;
  onGrantAccess: () => void;
  onGuaranteeRefund: (plan: GuaranteeTargetPlan) => void;
  onCancelPlan: (plan: GuaranteeTargetPlan) => void;
  onResumePlan: (planCode: string) => void;
  busyPlanCode?: string;
  onDelete: () => void;

  onOpenPayoutAccounts: () => void;
  onOpenEntityBalance: () => void;
  onOpenEntityPayouts: () => void;
  onOpenSubscriptionHistories: () => void;
  onOpenServiceAndPlans: () => void;

  payoutAccounts: PayoutAccountResponseDTO[] | null;
  payoutAccountsLoading: boolean;
  entityBalance: BalanceResponseDTO | null;
  entityBalanceLoading: boolean;
  entityPayouts: PayoutResponseDTO[] | null;
  entityPayoutsLoading: boolean;
  subscriptionHistories: PaymentHistoryGroupedDTO[] | null;
  subscriptionHistoriesLoading: boolean;
  serviceWithPlans: ServiceWithPlansDTO | null;
  serviceWithPlansLoading: boolean;
  sumPlans: SumPlanStatusEntryDTO[] | null;
  sumPlansLoading: boolean;
}

const EntityDrawer: React.FC<EntityDrawerProps> = ({
  open,
  onClose,
  loading,
  entityDetails,
  featureLinks,
  onEmailOwner,
  onImpersonate,
  impersonateLoading,
  onGrantAccess,
  onGuaranteeRefund,
  onCancelPlan,
  onResumePlan,
  busyPlanCode,
  onDelete,
  onOpenPayoutAccounts,
  onOpenEntityBalance,
  onOpenEntityPayouts,
  onOpenSubscriptionHistories,
  onOpenServiceAndPlans,
  payoutAccounts,
  payoutAccountsLoading,
  entityBalance,
  entityBalanceLoading,
  entityPayouts,
  entityPayoutsLoading,
  subscriptionHistories,
  subscriptionHistoriesLoading,
  serviceWithPlans,
  serviceWithPlansLoading,
  sumPlans,
  sumPlansLoading,
}) => {
  const balanceValue = entityBalance
    ? entityBalance.balances.length > 0
      ? formatAmount(
          entityBalance.balances[0].estimated_net_amount / 100,
          entityBalance.balances[0].currency
        )
      : "n/a"
    : undefined;

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={entityDetails?.entity_name || "Entity"}
      icon={HiOfficeBuilding}
      footer={
        entityDetails && (
          <div className="sticky bottom-0 left-0 right-0 -mx-4 -mb-4 border-t border-gray-200 bg-white px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <CustomButton
                onClick={onGrantAccess}
                variant="primary"
                size="sm"
                title="Grant access"
                icon="handsHelping"
              />
              <CustomButton
                onClick={onImpersonate}
                variant="outline"
                size="sm"
                title="Impersonate"
                icon="userPlusCurrent"
                isLoading={impersonateLoading}
                disabled={impersonateLoading}
              />
              {entityDetails.owner_email && (
                <CustomButton
                  onClick={onEmailOwner}
                  variant="outline"
                  size="sm"
                  title="Email owner"
                  icon="envelop"
                />
              )}
              <div className="ml-auto border-l border-gray-200 pl-2">
                <CustomButton
                  onClick={onDelete}
                  variant="danger"
                  size="sm"
                  title="Delete"
                  icon={<HiOutlineTrash size={18} />}
                />
              </div>
            </div>
          </div>
        )
      }
    >
      <>
        {loading && !entityDetails ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="xl" />
          </div>
        ) : !entityDetails ? (
          <p className="py-16 text-center text-sm text-gray-500">
            No entity selected.
          </p>
        ) : (
          <div className="flex h-full flex-col gap-5 pb-24">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center gap-3">
                <UserAvatar
                  email={entityDetails.owner_email}
                  name={entityDetails.entity_name}
                  sizeClass="w-10 h-10"
                />
                <div className="min-w-0">
                  <p className="truncate font-semibold text-gray-900">
                    {entityDetails.entity_name}
                  </p>
                  <div className="text-xs text-gray-500">
                    <Copy
                      target={entityDetails.entity_identification}
                      truncate
                      longVersion
                    />
                  </div>
                </div>
                <span className="ml-auto">
                  {getStatusBadge(entityDetails.entity_status)}
                </span>
              </div>

              <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                <dt className="text-gray-500">Users</dt>
                <dd className="tabular-nums text-gray-700">
                  {entityDetails.total_users}
                </dd>

                <dt className="text-gray-500">Country</dt>
                <dd className="text-gray-700">
                  <NoTranslate>
                    {entityDetails.owner_country_code || "N/A"}
                  </NoTranslate>
                </dd>

                <dt className="text-gray-500">Last connection</dt>
                <dd className="tabular-nums text-gray-700">
                  {formatDate(entityDetails.last_connection_datetime)}
                </dd>

                <dt className="text-gray-500">Owner email</dt>
                <dd className="flex min-w-0 flex-wrap items-center gap-2 text-gray-700">
                  {entityDetails.owner_email ? (
                    <NoTranslate>{entityDetails.owner_email}</NoTranslate>
                  ) : (
                    <span className="text-gray-400">No owner</span>
                  )}
                  {entityDetails.owner_user_is_active === false && (
                    <span title="The owner has not verified their email address. Impersonation will fail until the email is confirmed.">
                      <StatusBadge color="warning">
                        Owner unverified
                      </StatusBadge>
                    </span>
                  )}
                </dd>

                {entityDetails.entity_description && (
                  <>
                    <dt className="self-start text-gray-500">Description</dt>
                    <dd className="text-gray-700">
                      {entityDetails.entity_description}
                    </dd>
                  </>
                )}
              </dl>
            </div>

            <EntitySumPlansSection
              entries={sumPlans}
              loading={sumPlansLoading}
              onRefund={onGuaranteeRefund}
              onCancel={onCancelPlan}
              onResume={onResumePlan}
              busyPlanCode={busyPlanCode}
            />

            <FeatureLinksSection
              featureLinks={featureLinks}
              features={entityDetails.features}
            />

            <AcquisitionSection tracking={entityDetails.acquisition_tracking} />

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Billing details
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <SummaryChip
                  icon={<HiCreditCard size={18} />}
                  label="Payout accounts"
                  loading={payoutAccountsLoading}
                  value={payoutAccounts ? payoutAccounts.length : undefined}
                  onClick={onOpenPayoutAccounts}
                />
                <SummaryChip
                  icon={<HiCurrencyDollar size={18} />}
                  label="Entity balance"
                  loading={entityBalanceLoading}
                  value={balanceValue}
                  onClick={onOpenEntityBalance}
                />
                <SummaryChip
                  icon={<HiDocumentText size={18} />}
                  label="Entity payouts"
                  loading={entityPayoutsLoading}
                  value={entityPayouts ? entityPayouts.length : undefined}
                  onClick={onOpenEntityPayouts}
                />
                <SummaryChip
                  icon={<HiClock size={18} />}
                  label="Subscription histories"
                  loading={subscriptionHistoriesLoading}
                  value={
                    subscriptionHistories
                      ? subscriptionHistories.length
                      : undefined
                  }
                  onClick={onOpenSubscriptionHistories}
                />
                <SummaryChip
                  icon={<HiCreditCard size={18} />}
                  label="Service and plans"
                  loading={serviceWithPlansLoading}
                  value={
                    serviceWithPlans
                      ? `${serviceWithPlans.plans?.length ?? 0} plans`
                      : undefined
                  }
                  onClick={onOpenServiceAndPlans}
                />
              </div>
            </div>
          </div>
        )}
      </>
    </SideDrawer>
  );
};

export default EntityDrawer;
